'use client';

import { __awaiter, __spreadArray, __rest, __assign, __generator } from '../_virtual/_tslib.js';
import React__default, { useState, useCallback, useEffect } from 'react';
import AutoComplete from '../AutoComplete/index.js';
import { colors } from '../Colors/index.js';
import Col from '../LayoutSystem/Col.js';
import Row from '../LayoutSystem/Row.js';
import NTag from '../NTag/index.js';
import TextField from '../TextField/index.js';
import Typography from '../Typography/index.js';
import '../Utils/constants.js';
import { createTranslator, filterObjectArrayBasedOnObjectArray } from '../Utils/helpers.js';
import '../Cascader/constants.js';
import { getSuggestionSkills } from './services.js';
import translations from './translations.js';
import RemoveFillIcon from '../Images/RemoveFillIcon/index.js';
import { SkillContainer, Button } from './styles.js';
import Form from '../Form/index.js';

var LIMIT_SUGGESTION_SKILLS = 5;
function Skills(_a) {
    var _this = this;
    var skills = _a.skills, accessToken = _a.accessToken, langCode = _a.langCode, baseMsUrl = _a.baseMsUrl, userJobTitle = _a.userJobTitle, onChange = _a.onChange, _b = _a.limit, limit = _b === void 0 ? 5 : _b, _c = _a.isMobile, isMobile = _c === void 0 ? false : _c, _d = _a.hasRecommendSkills, hasRecommendSkills = _d === void 0 ? true : _d;
    /**
     * State
     * */
    var _e = useState([]), recommendSkills = _e[0], setRecommendSkills = _e[1];
    /**
     * Helper function
     * */
    var t = createTranslator({
        translations: translations,
        currentLanguage: langCode
    });
    /**
     * Api helper function
     * */
    var getSuggestion = useCallback(function (searchKeyworks, limit) { return __awaiter(_this, void 0, void 0, function () {
        var selectedSkills, suggestionSkills;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    selectedSkills = skills.map(function (skill) { return skill.skillName; }) || [];
                    return [4 /*yield*/, getSuggestionSkills(baseMsUrl, accessToken !== null && accessToken !== void 0 ? accessToken : '', searchKeyworks, selectedSkills, limit, langCode)];
                case 1:
                    suggestionSkills = _a.sent();
                    return [2 /*return*/, suggestionSkills];
            }
        });
    }); }, [accessToken, skills]);
    /**
     * Effect
     * */
    // Get recommend skills based on user's job title
    var getRecommendSkills = useCallback(function (value) { return __awaiter(_this, void 0, void 0, function () {
        var recommendSkills, filtedRecommendSkills;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, getSuggestion(value, LIMIT_SUGGESTION_SKILLS)];
                case 1:
                    recommendSkills = _a.sent();
                    filtedRecommendSkills = filterObjectArrayBasedOnObjectArray(recommendSkills, skills, 'skillId', [-1]);
                    setRecommendSkills(filtedRecommendSkills);
                    return [2 /*return*/];
            }
        });
    }); }, [skills]);
    useEffect(function () {
        getRecommendSkills(userJobTitle);
    }, [userJobTitle, getRecommendSkills]);
    /**
     * Handle logic and action
     * */
    var concatSelectedSkills = useCallback(function (incomingSkills) {
        if (skills.length >= limit) {
            return skills;
        }
        if (Array.isArray(incomingSkills)) {
            var skillsToAdd = __spreadArray([], incomingSkills, true);
            var containSizeOfSkills = limit - skills.length;
            if (containSizeOfSkills < skillsToAdd.length) {
                skillsToAdd = skillsToAdd.slice(0, containSizeOfSkills);
            }
            return __spreadArray([], skills, true).concat(skillsToAdd);
        }
        var result = __spreadArray([], skills, true);
        result.push(incomingSkills);
        return result;
    }, [skills]);
    var handleSelectedRecommendSkills = function (selectedSkills) {
        onChange(concatSelectedSkills(selectedSkills));
    };
    var handleSelectDropdown = function (selectedSkills) {
        if (selectedSkills.length > limit) {
            return;
        }
        onChange(selectedSkills);
    };
    var handleClearAllSkills = function () { return onChange([]); };
    return (React__default.createElement(Row, { gutter: { xs: [20, 0], sm: [40, 0] } },
        React__default.createElement(Col, null,
            React__default.createElement(AutoComplete, { multiple: true, fieldKey: 'skillId', fieldValue: 'skillName', options: function (_a) {
                    var keywords = _a.keywords;
                    return __awaiter(_this, void 0, void 0, function () {
                        var creatableSuggestion, suggestions;
                        return __generator(this, function (_b) {
                            switch (_b.label) {
                                case 0:
                                    creatableSuggestion = [];
                                    if (keywords !== '') {
                                        creatableSuggestion.push({
                                            skillName: keywords,
                                            skillId: -1,
                                            skillLevel: 0
                                        });
                                    }
                                    return [4 /*yield*/, getSuggestion(keywords)];
                                case 1:
                                    suggestions = _b.sent();
                                    return [2 /*return*/, creatableSuggestion.concat(suggestions)];
                            }
                        });
                    });
                }, value: skills, onChange: handleSelectDropdown, creatable: { enable: true, defaultKey: -1 }, renderInput: function (_a) {
                    var onDelete = _a.onDelete, params = __rest(_a, ["onDelete"]);
                    var isError = skills.length >= limit;
                    return (React__default.createElement(SkillContainer, { style: { position: 'relative' } },
                        React__default.createElement(TextField, __assign({ id: 'vnw-skills-input', hasError: isError, prefix: skills.map(function (value) { return (React__default.createElement(NTag, { key: value.skillId, label: value.skillName, onDelete: function () { return onDelete(value); } })); }), suffix: React__default.createElement("div", { style: { width: 16 } }), allowClear: false, placeholder: t('Add a skill'), styles: {
                                container: isMobile ? { maxHeight: 74, overflow: 'auto' } : {}
                            } }, params)),
                        skills.length > 0 ? (React__default.createElement(Button, { onClick: handleClearAllSkills, "aria-label": 'Delete all skills' },
                            React__default.createElement(RemoveFillIcon, { fill: colors.grey[60] }))) : null,
                        isError ? (React__default.createElement(Form.Error, { forceMsg: "".concat(t('You can get maximum'), " ").concat(limit, " ").concat(t('skills on profile')) })) : null));
                } })),
        recommendSkills.length > 0 && hasRecommendSkills === true ? (React__default.createElement(Col, null,
            React__default.createElement(Row, { gutter: { xs: [24, 0] }, justify: 'center' },
                React__default.createElement(Col, { xs: 'auto' },
                    React__default.createElement(Typography, { name: 'label', size: 'medium', variant: 'p' }, t('Or select from suggested list'))),
                React__default.createElement(Col, null,
                    React__default.createElement(Row, { gutter: 12, justify: 'center' }, recommendSkills.map(function (item) {
                        return (React__default.createElement(Col, { key: item.skillId, xs: 'auto' },
                            React__default.createElement(NTag, { label: item.skillName, variant: 'outline', onClick: function () { return handleSelectedRecommendSkills(item); } })));
                    }))),
                React__default.createElement(Col, { xs: 'auto' },
                    React__default.createElement(Typography, { variant: 'p', size: 'small', name: 'label', color: colors.primary[40], style: { cursor: 'pointer' }, onClick: function () { return handleSelectedRecommendSkills(recommendSkills); } }, t('Select all')))))) : null));
}

export { Skills as default };
//# sourceMappingURL=index.js.map
